import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Check,
  X,
  AlertCircle,
  Plus,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Package,
  FileSpreadsheet,
} from "lucide-react";

// Virtualized list component for performance with large datasets
function VirtualizedList({ items, itemHeight, renderItem, maxHeight }) {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  const totalHeight = items.length * itemHeight;
  const startIndex = Math.floor(scrollTop / itemHeight);
  const visibleCount = Math.ceil(maxHeight / itemHeight) + 2;
  const endIndex = Math.min(startIndex + visibleCount, items.length);
  const offsetY = startIndex * itemHeight;

  const visibleItems = items.slice(startIndex, endIndex);

  return (
    <div
      ref={containerRef}
      className="overflow-y-auto"
      style={{ maxHeight }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: "relative" }}>
        <div style={{ position: "absolute", top: offsetY, left: 0, right: 0 }}>
          {visibleItems.map((item, i) => renderItem(item, startIndex + i))}
        </div>
      </div>
    </div>
  );
}

export default function ImportReviewDialog({
  open,
  onOpenChange,
  analysisData,
  onApply,
  applying,
}) {
  const [selectedChanges, setSelectedChanges] = useState(new Set());
  const [insertAllNew, setInsertAllNew] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [newProductsExpanded, setNewProductsExpanded] = useState(true);
  const [changesExpanded, setChangesExpanded] = useState(true);

  // Initialize selection with all changes selected
  React.useEffect(() => {
    if (analysisData?.changed_products) {
      setSelectedChanges(new Set(analysisData.changed_products.map((_, i) => i)));
    }
  }, [analysisData]);

  const toggleChange = useCallback((index) => {
    setSelectedChanges((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const toggleExpand = useCallback((index) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (analysisData?.changed_products) {
      setSelectedChanges(new Set(analysisData.changed_products.map((_, i) => i)));
    }
  }, [analysisData]);

  const deselectAll = useCallback(() => {
    setSelectedChanges(new Set());
  }, []);

  const handleApply = useCallback(() => {
    const selectedUpdates = analysisData.changed_products.filter((_, i) =>
      selectedChanges.has(i)
    );
    onApply({
      new_products: insertAllNew ? analysisData.new_products : [],
      updates: selectedUpdates,
      insert_all_new: insertAllNew,
    });
  }, [analysisData, selectedChanges, insertAllNew, onApply]);

  const stats = useMemo(() => {
    if (!analysisData) return null;
    return {
      newCount: analysisData.new_products_count || 0,
      changedCount: analysisData.changed_products_count || 0,
      unchangedCount: analysisData.unchanged_count || 0,
      selectedCount: selectedChanges.size,
      skippedCount:
        (analysisData.skipped_empty_barcode || 0) +
        (analysisData.skipped_duplicate_in_excel || 0),
    };
  }, [analysisData, selectedChanges]);

  if (!analysisData) return null;

  const hasChanges = stats.newCount > 0 || stats.changedCount > 0;
  const newProducts = analysisData.new_products || [];
  const changedProducts = analysisData.changed_products || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="p-0 gap-0 flex flex-col"
        style={{ 
          width: "95vw", 
          maxWidth: "1400px", 
          height: "90vh",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b shrink-0 bg-white">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="w-5 h-5 text-[#9E6D5B]" />
            Import Review - Smart Differential Update
          </DialogTitle>
        </DialogHeader>

        {/* Summary Stats Bar */}
        <div className="px-6 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b shrink-0">
          <div className="flex flex-wrap gap-3 justify-center">
            <StatCard value={stats.newCount} label="New Products" color="emerald" />
            <StatCard value={stats.changedCount} label="With Changes" color="orange" />
            <StatCard value={stats.selectedCount} label="Selected" color="blue" />
            <StatCard value={stats.unchangedCount} label="Unchanged" color="gray" />
            <StatCard value={stats.skippedCount} label="Skipped" color="red" />
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* New Products Section */}
          {newProducts.length > 0 && (
            <div className="border-b">
              {/* Section Header */}
              <div 
                className="flex items-center justify-between px-6 py-3 bg-emerald-50/50 cursor-pointer hover:bg-emerald-50 transition-colors"
                onClick={() => setNewProductsExpanded(!newProductsExpanded)}
              >
                <div className="flex items-center gap-2">
                  {newProductsExpanded ? (
                    <ChevronDown className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-emerald-600" />
                  )}
                  <Plus className="w-4 h-4 text-emerald-600" />
                  <span className="font-semibold text-sm text-emerald-800">
                    New Products ({newProducts.length})
                  </span>
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                    Will be added
                  </Badge>
                </div>
                <label 
                  className="flex items-center gap-2 text-xs cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={insertAllNew}
                    onCheckedChange={setInsertAllNew}
                    data-testid="insert-all-new-checkbox"
                  />
                  <span className="text-emerald-700 font-medium">Insert all new products</span>
                </label>
              </div>

              {/* New Products List - Full List with Virtualization */}
              {newProductsExpanded && (
                <div className="px-6 py-3 bg-white">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-emerald-50 rounded-t text-[10px] uppercase tracking-wider text-emerald-700 font-semibold border border-emerald-100">
                    <div className="col-span-2">Barcode</div>
                    <div className="col-span-6">Product Name</div>
                    <div className="col-span-2">Brand</div>
                    <div className="col-span-1 text-right">MRP</div>
                    <div className="col-span-1 text-right">Cost</div>
                  </div>
                  
                  {/* Products List - Full with scroll */}
                  <div 
                    className="border border-t-0 border-emerald-100 rounded-b overflow-y-auto"
                    style={{ maxHeight: "250px" }}
                  >
                    {newProducts.map((p, i) => (
                      <div
                        key={p.auto_barcode || `new-${i}`}
                        className={`grid grid-cols-12 gap-2 px-3 py-2 text-xs items-center ${
                          i % 2 === 0 ? "bg-white" : "bg-emerald-50/30"
                        } hover:bg-emerald-50 transition-colors border-b border-emerald-50 last:border-b-0`}
                      >
                        <div className="col-span-2 font-mono text-[11px] text-emerald-700">
                          {p.auto_barcode}
                        </div>
                        <div className="col-span-6 truncate font-medium" title={p.product}>
                          {p.product}
                        </div>
                        <div className="col-span-2 text-muted-foreground truncate text-[11px]" title={p.brand_name}>
                          {p.brand_name || "—"}
                        </div>
                        <div className="col-span-1 text-right font-mono text-[11px]">
                          ₹{p.mrp || 0}
                        </div>
                        <div className="col-span-1 text-right font-mono text-[11px] text-muted-foreground">
                          ₹{p.last_purchase_cost || p.purchase_price || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Count indicator */}
                  <div className="text-[10px] text-emerald-600 mt-2 text-center">
                    Showing all {newProducts.length} new product{newProducts.length !== 1 ? "s" : ""}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Changed Products Section */}
          {changedProducts.length > 0 && (
            <div className="border-b">
              {/* Section Header */}
              <div 
                className="flex items-center justify-between px-6 py-3 bg-orange-50/50 cursor-pointer hover:bg-orange-50 transition-colors"
                onClick={() => setChangesExpanded(!changesExpanded)}
              >
                <div className="flex items-center gap-2">
                  {changesExpanded ? (
                    <ChevronDown className="w-4 h-4 text-orange-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-orange-600" />
                  )}
                  <RefreshCw className="w-4 h-4 text-orange-600" />
                  <span className="font-semibold text-sm text-orange-800">
                    Products with Changes ({changedProducts.length})
                  </span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAll}
                    className="h-7 text-xs text-orange-700 hover:text-orange-800 hover:bg-orange-100"
                    data-testid="select-all-btn"
                  >
                    <Check className="w-3 h-3 mr-1" /> Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={deselectAll}
                    className="h-7 text-xs text-orange-700 hover:text-orange-800 hover:bg-orange-100"
                    data-testid="deselect-all-btn"
                  >
                    <X className="w-3 h-3 mr-1" /> Deselect All
                  </Button>
                </div>
              </div>

              {/* Changed Products Table */}
              {changesExpanded && (
                <div className="px-6 py-3 bg-white">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-orange-50 rounded-t text-[10px] uppercase tracking-wider text-orange-700 font-semibold border border-orange-100">
                    <div className="col-span-1 text-center">Select</div>
                    <div className="col-span-2">Barcode</div>
                    <div className="col-span-4">Product</div>
                    <div className="col-span-4">Changes</div>
                    <div className="col-span-1 text-center">#</div>
                  </div>
                  
                  {/* Changed Products List */}
                  <div 
                    className="border border-t-0 border-orange-100 rounded-b overflow-y-auto"
                    style={{ maxHeight: "350px" }}
                  >
                    {changedProducts.map((item, index) => (
                      <div
                        key={item.auto_barcode || `chg-${index}`}
                        className={`border-b border-orange-50 last:border-b-0 ${
                          selectedChanges.has(index) ? "bg-orange-50/30" : "bg-white opacity-60"
                        }`}
                        data-testid={`change-item-${index}`}
                      >
                        {/* Main Row */}
                        <div 
                          className="grid grid-cols-12 gap-2 px-3 py-2 items-center cursor-pointer hover:bg-orange-50/50 transition-colors"
                          onClick={() => toggleExpand(index)}
                        >
                          {/* Checkbox */}
                          <div 
                            className="col-span-1 flex justify-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selectedChanges.has(index)}
                              onCheckedChange={() => toggleChange(index)}
                              data-testid={`change-checkbox-${index}`}
                            />
                          </div>
                          
                          {/* Barcode */}
                          <div className="col-span-2 font-mono text-[11px] text-orange-700">
                            {item.auto_barcode}
                          </div>
                          
                          {/* Product Name */}
                          <div className="col-span-4 text-xs font-medium truncate" title={item.product}>
                            {item.product}
                          </div>
                          
                          {/* Changed Fields */}
                          <div className="col-span-4 flex flex-wrap gap-1">
                            {item.changes.map((change, cIdx) => (
                              <Badge
                                key={`${change.field}-${cIdx}`}
                                variant="outline"
                                className={`text-[9px] px-1.5 py-0 ${getChangeFieldColor(change.field)}`}
                              >
                                {change.field_label}
                              </Badge>
                            ))}
                          </div>
                          
                          {/* Change Count + Expand */}
                          <div className="col-span-1 flex items-center justify-center gap-1">
                            <span className="text-[10px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">
                              {item.changes.length}
                            </span>
                            {expandedItems.has(index) ? (
                              <ChevronDown className="w-3 h-3 text-orange-400" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-orange-400" />
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Detail */}
                        {expandedItems.has(index) && (
                          <div className="px-3 pb-3 pt-1 bg-orange-50/20">
                            <div className="bg-white rounded border border-orange-100 overflow-hidden">
                              {/* Detail Header */}
                              <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-orange-50/50 text-[9px] uppercase tracking-wider text-orange-600 font-semibold">
                                <div className="col-span-3">Field</div>
                                <div className="col-span-4">Old Value</div>
                                <div className="col-span-1 text-center"></div>
                                <div className="col-span-4">New Value</div>
                              </div>
                              {item.changes.map((change, cIdx) => (
                                <div
                                  key={`${change.field}-detail-${cIdx}`}
                                  className="grid grid-cols-12 gap-2 px-3 py-2 text-xs items-center border-t border-orange-50"
                                >
                                  <div className="col-span-3">
                                    <Badge
                                      variant="outline"
                                      className={`text-[9px] ${getChangeFieldColor(change.field)}`}
                                    >
                                      {change.field_label}
                                    </Badge>
                                  </div>
                                  <div className="col-span-4 px-2 py-1 bg-red-50 rounded border border-red-100 text-red-700 truncate font-mono text-[11px]" title={change.old_value}>
                                    <span className="line-through">{change.old_value || "(empty)"}</span>
                                  </div>
                                  <div className="col-span-1 flex justify-center">
                                    <ArrowRight className="w-4 h-4 text-orange-400" />
                                  </div>
                                  <div className="col-span-4 px-2 py-1 bg-emerald-50 rounded border border-emerald-100 text-emerald-700 truncate font-mono text-[11px]" title={change.new_value}>
                                    {change.new_value || "(empty)"}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Count indicator */}
                  <div className="text-[10px] text-orange-600 mt-2 text-center">
                    {stats.selectedCount} of {changedProducts.length} product{changedProducts.length !== 1 ? "s" : ""} selected for update
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No Changes Message */}
          {!hasChanges && (
            <div className="py-16 text-center">
              <Package className="w-16 h-16 mx-auto text-gray-200 mb-4" />
              <div className="text-sm text-muted-foreground font-medium">
                No changes detected
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                All products in the Excel file match the existing inventory.
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {stats.unchangedCount} products unchanged, {stats.skippedCount} rows skipped
              </div>
            </div>
          )}
        </div>

        {/* Sticky Footer - Action Bar */}
        <div className="shrink-0 px-6 py-4 border-t bg-gradient-to-r from-slate-50 to-slate-100 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between">
            {/* Left: Summary */}
            <div className="flex items-center gap-4 text-sm">
              {insertAllNew && stats.newCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span className="text-emerald-700 font-semibold">+{stats.newCount} new</span>
                </div>
              )}
              {stats.selectedCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  <span className="text-orange-700 font-semibold">
                    {stats.selectedCount} update{stats.selectedCount > 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {!insertAllNew && stats.newCount === 0 && stats.selectedCount === 0 && (
                <span className="text-muted-foreground text-xs">No changes selected</span>
              )}
            </div>
            
            {/* Right: Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={applying}
                className="px-6"
                data-testid="cancel-import-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApply}
                disabled={
                  applying ||
                  (!insertAllNew && stats.selectedCount === 0) ||
                  (insertAllNew && stats.newCount === 0 && stats.selectedCount === 0)
                }
                className="bg-[#9E6D5B] hover:bg-[#8a5f4f] px-6 min-w-[140px]"
                data-testid="apply-changes-btn"
              >
                {applying ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Apply Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper component for stat cards
function StatCard({ value, label, color }) {
  const colors = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    gray: "bg-gray-50 border-gray-200 text-gray-500",
    red: "bg-red-50 border-red-200 text-red-600",
  };
  
  return (
    <div className={`px-4 py-2 rounded-lg border ${colors[color]} min-w-[100px] text-center`}>
      <div className="text-xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}

// Helper function for field-specific colors
function getChangeFieldColor(field) {
  const criticalFields = ["mrp", "auto_barcode", "manual_barcode"];
  const priceFields = ["purchase_price", "last_purchase_cost", "wholesale_price"];
  
  if (criticalFields.includes(field)) {
    return "border-red-300 bg-red-50 text-red-700";
  }
  if (priceFields.includes(field)) {
    return "border-amber-300 bg-amber-50 text-amber-700";
  }
  return "border-orange-200 bg-orange-50 text-orange-700";
}
